/** Real backend service clients (typed). Shapes verified against the live dev
 *  stack. Paths carry the full prefix (the dev proxy forwards unchanged). */

import { apiGet, apiPost, apiPatch, apiDelete } from "@/api/client";
import type { ThemeTokens } from "@/editor/theme/ThemeStyleInjector";
import type { Insight, OvernightItem } from "@/mock/types";

export type Range = "24h" | "7d" | "30d" | "90d";
export function toPeriod(r: Range): string {
  return r === "24h" ? "day" : r === "7d" ? "week" : "month";
}

/* ------------------------------- analytics -------------------------------- */
export type SiteType = "web" | "ios" | "android" | "server";
export interface Site {
  site_id: string;
  int_alias: number;
  name: string;
  type: SiteType;
  write_key_last4: string;
  allowed_origins: string[];
  urls: string[];
  created_at: string;
  revoked_at?: string | null;
}
export interface SiteList {
  sites: Site[];
}

export interface SummaryComparison {
  visits: number;
  unique_visitors: number;
  bounce_rate: number;
  conversion_rate: number;
  avg_session_seconds: number;
  pages_per_visit: number;
}
export interface Summary {
  visits: number;
  unique_visitors: number;
  bounce_rate: number;
  conversion_rate: number;
  avg_session_seconds: number;
  pages_per_visit: number;
  referrers?: { source: string; visits: number }[];
  devices?: Record<string, number>;
  seeded?: boolean;
  /** Prior-period values (present when summary requested with compare). (#18) */
  previous?: SummaryComparison;
}
export interface TimePoint {
  t: number;
  value: number;
}
export interface Timeseries {
  points: TimePoint[];
  seeded?: boolean;
}
export interface Realtime {
  active: number;
  recent: { ts: number; type: string; path: string }[];
}
export interface NameVisits {
  items: { source?: string; device?: string; country?: string; country_name?: string; visits: number }[];
}
export interface EventsResp {
  items: { category: string; action: string; name: string; count: number; total_value: number }[];
}
export interface FunnelResp {
  steps: { step: number; kind: string; label: string; visitors: number; reached_pct: number; drop_pct: number }[];
  entered: number;
  completed: number;
  overall_pct: number;
  seeded?: boolean;
}
export interface HeatmapResp {
  points: { x: number; y: number; weight: number }[];
  max_weight: number;
  samples: number;
  seeded?: boolean;
}
export interface ScrollResp {
  buckets: { depth: number; reached_pct: number }[];
}
export interface ReplayList {
  sessions: {
    session_id: string;
    started: number;
    last: number;
    duration_seconds: number;
    event_count: number;
    device: string;
    source: string;
  }[];
  seeded?: boolean;
}
export interface ReplayDetail {
  events: unknown[];
  source: "web" | "mobile";
}
/** Nara's Today feed. Item shapes are the single source of truth in mock/types
 *  — the analytics-svc /insights + /digest endpoints return exactly these, with
 *  `seeded:true` when the property has no real traffic yet (demo fallback). */
export interface InsightsReport {
  items: Insight[];
  seeded: boolean;
}
export interface DigestReport {
  items: OvernightItem[];
  seeded: boolean;
}

const A = "/analytics/v1";
const siteBase = (id: string) => `${A}/sites/${id}`;
const pageBase = (id: string) => `${A}/pages/${id}`;

/** The read panels, parameterized by scope base path. The backend exposes the
 *  SAME panel set + response shapes under /sites/{id}/* (whole property) and
 *  /pages/{id}/* (one page) — so the "filter by page" feature just swaps base. */
function reads(base: (id: string) => string) {
  return {
    summary: (id: string, period: string) =>
      apiGet<Summary>(`${base(id)}/summary?period=${period}&compare=1`),
    timeseries: (id: string, metric: string, period: string) =>
      apiGet<Timeseries>(`${base(id)}/timeseries?metric=${metric}&period=${period}`),
    realtime: (id: string) => apiGet<Realtime>(`${base(id)}/realtime`),
    sources: (id: string, period: string) => apiGet<NameVisits>(`${base(id)}/sources?period=${period}`),
    devices: (id: string, period: string) => apiGet<NameVisits>(`${base(id)}/devices?period=${period}`),
    geo: (id: string, period: string) => apiGet<NameVisits>(`${base(id)}/geo?period=${period}`),
    events: (id: string, period: string) => apiGet<EventsResp>(`${base(id)}/events?period=${period}`),
    funnels: (id: string, period: string, steps?: string) =>
      apiGet<FunnelResp>(
        `${base(id)}/funnels?period=${period}${steps ? `&steps=${encodeURIComponent(steps)}` : ""}`,
      ),
    heatmap: (id: string, period: string) => apiGet<HeatmapResp>(`${base(id)}/heatmap?period=${period}`),
    scroll: (id: string, period: string) => apiGet<ScrollResp>(`${base(id)}/scroll?period=${period}`),
    replays: (id: string) => apiGet<ReplayList>(`${base(id)}/replays`),
    replay: (id: string, sessionId: string) => apiGet<ReplayDetail>(`${base(id)}/replays/${sessionId}`),
  };
}

/** A web property and its newly-minted write key. The plaintext key is returned
 *  ONCE (on create or rotate) — only the last4 is ever stored/shown afterwards. */
export interface SiteWithKey extends Site {
  write_key: string;
}

/** Whole-property analytics (default Signals scope). */
export const analytics = {
  ...reads(siteBase),
  listSites: () => apiGet<SiteList>(`${A}/sites`),
  createSite: (body: { name: string; type: SiteType; allowed_origins?: string[]; urls?: string[] }) =>
    apiPost<SiteWithKey>(`${A}/sites`, body),
  /** Rotate a property's write key. Returns the NEW plaintext key once; the old
   *  key stops working immediately (server clears its hash + cache). */
  rotateSiteKey: (siteId: string) =>
    apiPost<{ write_key: string; write_key_last4: string }>(`${A}/sites/${siteId}/rotate-key`, {}),
  /** Revoke (soft-delete) a property — its keys stop working immediately. */
  revokeSite: (siteId: string) => apiDelete(`${A}/sites/${siteId}`),
  /** Patch property metadata (name / allowed origins / urls). */
  patchSite: (
    siteId: string,
    body: Partial<{ name: string; allowed_origins: string[]; urls: string[] }>,
  ) => apiPatch<Site>(`${A}/sites/${siteId}`, body),
  /** Nara's Today feed — insights + overnight digest (whole-property scope). */
  insights: (id: string, period: string) =>
    apiGet<InsightsReport>(`${siteBase(id)}/insights?period=${period}`),
  digest: (id: string, period: string) =>
    apiGet<DigestReport>(`${siteBase(id)}/digest?period=${period}`),
};

/** Single-page analytics — same shapes, used when Signals is filtered to a page. */
export const pageAnalytics = reads(pageBase);

/* ----------------------------- dashboards (#20) --------------------------- */
// Per-user custom dashboards within an org. Persisted by analytics-svc under
// /analytics/v1/dashboards (JWT-gated; scoped by BOTH org + caller user id), so
// the marketer's chosen widgets follow them across browsers. `config` is
// arbitrary JSONB — the frontend stores { widgets: string[] }.
export interface Dashboard {
  id: string;
  name: string;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}
export interface DashboardList {
  dashboards: Dashboard[];
}
export const dashboards = {
  list: () => apiGet<DashboardList>(`${A}/dashboards`),
  create: (body: { name: string; config?: Record<string, unknown> }) =>
    apiPost<Dashboard>(`${A}/dashboards`, body),
  get: (id: string) => apiGet<Dashboard>(`${A}/dashboards/${id}`),
  update: (id: string, body: Partial<{ name: string; config: Record<string, unknown> }>) =>
    apiPatch<Dashboard>(`${A}/dashboards/${id}`, body),
  remove: (id: string) => apiDelete(`${A}/dashboards/${id}`),
};

/* -------------------------------- builder --------------------------------- */
export interface Project {
  id: string;
  name: string;
  org_id: string;
  created_at: string;
}
export interface Page {
  id: string;
  slug: string;
  title: string;
  project_id: string;
  locale: string;
  status: "draft" | "staging" | "published" | "archived";
  current_version_id: string;
  domain?: string;
  /** SEO metadata — persisted by PATCH /pages/{id}; getPage returns them too. */
  seo_description?: string;
  og_image_url?: string;
  updated_at: string;
}
export interface ComponentNode {
  node_id: string;
  component: { name: string; version: string };
  props: Record<string, unknown>;
  children?: ComponentNode[];
}
export interface ComponentTree {
  root: ComponentNode;
}
export interface PageVersion {
  id: string;
  page_id: string;
  version_n: number;
  status: Page["status"];
  tree: ComponentTree;
  message?: string;
  author_user_id?: string;
  created_at: string;
}

const B = "/api/v1";

/** A DNS record the user must add at their registrar (A / CNAME / TXT). */
export interface DnsRecord {
  type: string;
  host: string;
  value: string;
}
/** One custom domain as returned by the builder-api domains endpoints. The
 *  same shape backs listDomains' items, createDomain and verifyDomain.
 *  `records` is keyed (a / cname / txt), each a full {type,host,value}. */
export interface DomainItem {
  id: string;
  hostname: string;
  status: string;
  records?: Record<string, DnsRecord>;
  verification_token?: string;
}

/** A person with access to a project (Team/Access). Matches builder-api's
 *  Member JSON exactly. Invites start as status "pending"; accepted/seeded
 *  members are "active". org_id is the tenant scope, omitted from the API JSON. */
export interface Member {
  id: string;
  project_id: string;
  email: string;
  role: string; // owner | editor | viewer
  status: string; // active | pending
  added_at: string;
}

/** One activity/audit-log entry (who did what, when). */
export interface AuditEntry {
  id: number;
  actor: string;
  action: string;
  entity: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export const builder = {
  listProjects: () => apiGet<{ items: Project[] }>(`${B}/projects`),
  /** Recent activity for the org, newest first; optional entity filter. */
  listAuditLog: (params?: { entity?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.entity) q.set("entity", params.entity);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return apiGet<{ items: AuditEntry[] }>(`${B}/audit-log${qs ? `?${qs}` : ""}`);
  },
  listPages: (projectId: string) => apiGet<Page[]>(`${B}/projects/${projectId}/pages`),
  getPage: (id: string) => apiGet<Page & { current_version?: PageVersion }>(`${B}/pages/${id}`),
  getVersion: (pageId: string, versionId: string) =>
    apiGet<PageVersion>(`${B}/pages/${pageId}/versions/${versionId}`),
  saveVersion: (pageId: string, versionId: string, tree: unknown, message?: string) =>
    apiPatch<PageVersion>(`${B}/pages/${pageId}/versions/${versionId}`, { tree, message }),
  createPage: (projectId: string, body: { title: string; slug?: string; domain: string; locale?: string }) =>
    apiPost<Page>(`${B}/projects/${projectId}/pages`, body),
  /** Patch page-level metadata. The builder-api updatePage handler (PATCH
   *  /pages/{id}) persists every field here — title, seo_description,
   *  og_image_url, domain, slug and status. JSON keys mirror
   *  model.UpdatePageRequest exactly. */
  updatePage: (
    pageId: string,
    body: {
      title?: string;
      slug?: string;
      status?: Page["status"];
      domain?: string;
      seo_description?: string;
      og_image_url?: string;
    },
  ) => apiPatch<Page>(`${B}/pages/${pageId}`, body),
  listDomains: (projectId: string) =>
    apiGet<{ items: DomainItem[] }>(`${B}/projects/${projectId}/domains`),
  createDomain: (projectId: string, body: { hostname: string }) =>
    apiPost<DomainItem>(`${B}/projects/${projectId}/domains`, body),
  verifyDomain: (projectId: string, domainId: string) =>
    apiPost<DomainItem>(`${B}/projects/${projectId}/domains/${domainId}/verify`, {}),
  listMembers: (projectId: string) =>
    apiGet<{ items: Member[] }>(`${B}/projects/${projectId}/members`),
  inviteMember: (projectId: string, body: { email: string; role?: string }) =>
    apiPost<Member>(`${B}/projects/${projectId}/members`, body),
  publish: (pageId: string, versionId: string, environment: "staging" | "prod", message?: string) =>
    apiPost<{ status: string }>(`${B}/pages/${pageId}/versions/${versionId}:publish`, { environment, message }),
  /** Delete a page (and its versions, via the DB cascade). Returns 204. */
  deletePage: (pageId: string) => apiDelete(`${B}/pages/${pageId}`),
  /** Duplicate a page — clones its tree + current version into a new draft.
   *  Optional title override (defaults to "Copy of <title>"). */
  duplicatePage: (pageId: string, body?: { title?: string }) =>
    apiPost<Page>(`${B}/pages/${pageId}:duplicate`, body ?? {}),
  /** List a page's versions (newest first) — backs the version-history UI. */
  listVersions: (pageId: string) => apiGet<{ items: PageVersion[]; next_cursor?: string }>(`${B}/pages/${pageId}/versions`),
  /** Restore an earlier version: copies its tree into a NEW draft (non-destructive)
   *  and points the page at it. Returns the new draft version. Publish to go live. */
  restoreVersion: (pageId: string, versionId: string) =>
    apiPost<PageVersion>(`${B}/pages/${pageId}/versions/${versionId}/restore`, {}),

  // BC-2 Templates
  listTemplates: (projectId: string, params?: { category?: string; kind?: string }) => {
    const q = new URLSearchParams();
    if (params?.category && params.category !== "All") q.set("category", params.category);
    if (params?.kind) q.set("kind", params.kind);
    const qs = q.toString() ? `?${q}` : "";
    return apiGet<TemplateList>(`${B}/projects/${projectId}/templates${qs}`);
  },
  getTemplate: (id: string) => apiGet<Template>(`${B}/templates/${id}`),
  instantiateTemplate: (id: string, body: { project_id: string; title?: string; slug?: string; domain?: string }) =>
    apiPost<Page>(`${B}/templates/${id}:instantiate`, body),
  saveAsTemplate: (pageId: string, body: { name?: string; category?: string; blurb?: string; thumbnail_colors?: string[] }) =>
    apiPost<Template>(`${B}/pages/${pageId}:save-as-template`, body),
};

export interface Template {
  id: string;
  name: string;
  slug: string;
  kind: "prebuilt" | "custom" | "published";
  category: string;
  blurb: string;
  thumbnail_colors: string[];
  source_page_id?: string;
  published_views: number;
  created_at: string;
  updated_at: string;
  tree?: ComponentTree;
}
export interface TemplateList {
  items: Template[];
  next_cursor?: string;
}

/* -------------------------- themes ("designs") ---------------------------- */
// Page-design themes: 12 semantic color tokens + typography, applied to the
// live page and the Make preview via `--dl-*` CSS vars. Backed by builder-api
// (GET/POST /projects/:id/themes, PATCH /themes/:id) — verified live.
export interface Theme {
  id: string;
  project_id: string;
  name: string;
  tokens: ThemeTokens;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}
export interface ThemeList {
  items: Theme[];
}
export const themes = {
  list: (projectId: string) => apiGet<ThemeList>(`${B}/projects/${projectId}/themes`),
  active: (projectId: string) => apiGet<Theme>(`${B}/projects/${projectId}/themes/active`),
  create: (projectId: string, body: { name: string; tokens: ThemeTokens; activate?: boolean }) =>
    apiPost<Theme>(`${B}/projects/${projectId}/themes`, body),
  update: (themeId: string, body: { tokens?: ThemeTokens; name?: string; activate?: boolean }) =>
    apiPatch<Theme>(`${B}/themes/${themeId}`, body),
  /** Delete a saved design. Server refuses (409) on the active theme and
   *  returns 204 on success. */
  delete: (themeId: string) => apiDelete(`${B}/themes/${themeId}`),
};

/** AI palette suggestion (DeepSeek-backed; returns the same token shape). */
export const aiDesign = {
  palette: (body: { vibe: string; industry?: string; dark_mode?: boolean }) =>
    apiPost<{ ok: boolean; tokens?: ThemeTokens; error?: string }>(`/ai/v1/palette`, body),
};

/* -------------------------------- forms ---------------------------------- */
// form-gateway (proxied at /forms → :8083). Forms are defined inline in the page
// tree via the Form block (formId + fields); submissions land here.
export interface FormSubmission {
  id: string;
  form_id: string;
  page_id?: string;
  version_id?: string;
  values: Record<string, unknown>;
  submitted_at: string;
  delivery_status?: string;
}
export const forms = {
  /** Submissions for one form (newest first), org-scoped. Backs the in-Make
   *  submissions viewer (#19). form-gateway admin route is /forms/v1/... */
  listSubmissions: (formId: string) =>
    apiGet<{ items: FormSubmission[] }>(`/forms/v1/forms/${formId}/submissions`),
};

/** SEO metadata derived from a page's visible copy (ai-agent /ai/v1/seo). */
export interface SeoMeta {
  title: string;
  description: string;
  keywords: string[];
  og_title?: string;
  og_description?: string;
}

/** One-shot AI assists used from editor buttons (ai-agent /ai/v1/*). */
export const aiAssist = {
  /** Rewrite one text prop into N stronger alternatives (#2 / A/B seeds #13). */
  copy: (body: { current: string; field_label?: string; tone?: string; audience?: string; n?: number }) =>
    apiPost<{ ok: boolean; variants?: string[]; error?: string }>(`/ai/v1/copy`, body),
  /** Generate SEO metadata from a page's content (#5). */
  seo: (body: { page_id?: string; version_id?: string; content?: string[] }) =>
    apiPost<{ ok: boolean; seo?: SeoMeta; error?: string }>(`/ai/v1/seo`, body),
  /** Build a whole page from a one-line brief (#12). Returns a Proposal. */
  generatePage: (body: {
    brief: string;
    page_id?: string;
    version_id?: string;
    audience?: string;
    goal?: string;
    registry_spec?: unknown[];
  }) => apiPost<{ ok: boolean; proposal?: unknown; error?: string }>(`/ai/v1/generate-page`, body),
};

/* ------------------------------- identity --------------------------------- */
export interface Entitlements {
  plan: string;
  page_limit: number;
  pages_used: number;
}
const ID = "/identity/v1";
export const identity = {
  entitlements: () => apiGet<Entitlements>(`${ID}/entitlements`),
  devPlan: (plan: string) => apiPost<unknown>(`${ID}/dev/plan`, { plan }),
};

/* ------------------------------ experiments (A/B) ------------------------- */
// experiment-svc (proxied at /experiments → :8085). Page-scoped: there is no
// site/property-level list, so the Signals panel aggregates across the project's
// pages. Stats come from analytics-svc (/analytics/v1/experiments/{id}/stats).
export type ExperimentStatus = "draft" | "running" | "paused" | "concluded";
export interface VariantOp {
  node_id: string;
  prop: string;
  value: unknown;
}
export interface ExperimentVariant {
  id: string;
  is_control: boolean;
  weight: number;
  label: string;
  ops?: VariantOp[];
}
export interface ExperimentGoal {
  event_name: string;
  primary: boolean;
}
export interface Experiment {
  id: string;
  page_id: string;
  name: string;
  hypothesis?: string;
  scope?: { node_id?: string; page_id?: string; domain?: string };
  status: ExperimentStatus;
  owner_user_id?: string;
  allocation: { traffic_pct: number; audience_filter?: string };
  variants: ExperimentVariant[];
  goals?: ExperimentGoal[];
  winner_variant_id?: string;
  started_at?: string;
  stopped_at?: string;
}
export interface ExperimentList {
  items: Experiment[];
}
export interface VariantStat {
  variant_id: string;
  is_control: boolean;
  exposures: number;
  conversions: number;
  conversion_rate: number;
  credible_interval_low: number;
  credible_interval_high: number;
  prob_beat_control: number | null;
}
export interface ExperimentStats {
  experiment_id: string;
  variants: VariantStat[];
}
export interface CreateExperimentBody {
  page_id: string;
  name: string;
  hypothesis?: string;
  scope?: { node_id?: string };
  variants: ExperimentVariant[];
  goals: ExperimentGoal[];
  allocation: { traffic_pct: number };
}

const E = "/experiments/v1";
export const experiments = {
  listByPage: (pageId: string) => apiGet<ExperimentList>(`${E}/pages/${pageId}/experiments`),
  create: (body: CreateExperimentBody) => apiPost<Experiment>(`${E}/experiments`, body),
  setStatus: (experimentId: string, status: ExperimentStatus, winnerVariantId?: string) =>
    apiPatch<Experiment>(
      `${E}/experiments/${experimentId}/status`,
      winnerVariantId ? { status, winner_variant_id: winnerVariantId } : { status },
    ),
  stats: (experimentId: string) => apiGet<ExperimentStats>(`${A}/experiments/${experimentId}/stats`),
};

export { apiDelete, apiPatch };
